/**
 * The one API surface for the web app. OWNER: web-frontend-engineer.
 *
 * Everything goes through `@pt/api-client` with an INJECTED transport, which is
 * the seam D-025 boundary 2 asks for: web passes the browser `fetch`, the tests
 * pass a stub, and nothing in this file touches a global except at the one call
 * site below.
 *
 * There is NO mock transport in this app. Every screen is developed and
 * verified against the real seeded server, which is what makes "it runs" a
 * claim rather than a hope.
 */

import { ApiRejection, ApiUnreachable, createApiClient, type FetchImpl } from '@pt/api-client';
import type {
  ConfigEvalRequest,
  CreateMeasurementRecordRequest,
  PlaceOrderRequest,
  PlaceOrderResponse,
  PromiseRequest,
  SessionResponse,
  SignInRequest,
  StockMoveRequest,
  TransitionRequest,
  TransitionResponse,
  LeadDetail,
  LeadRow,
} from '@pt/core';
import type {
  AtelierClientPayload,
  AtelierClientRow,
  PhotographSet,
  PhotographSetsPayload,
  AtelierClothPayload,
  AtelierClothRow,
  AtelierOrderPayload,
  ClothDetailPayload,
  ClothInterestRow,
  EvaluationPayload,
  FabricListPayload,
  HomePayload,
  MeasurementsPayload,
  OrderPayload,
  OrderSummary,
  PaymentCreated,
  PaymentIntentRow,
  PaymentMethodsPayload,
  PaymentsPayload,
  GlossaryPayload,
  GlossaryTerm,
  RetentionPayload,
  QueuePayload,
  ReviewPayload,
  SwatchQueueRow,
  CutTicketsResponse,
  SketchDrawing,
  TicketSketch,
} from './contracts.ts';
import { adaptAtelierOrder, adaptClothDetail, adaptFabricList, adaptHome, adaptOrder, adaptReview } from './adapt.ts';

export { ApiRejection, ApiUnreachable };

/**
 * The browser `fetch`, injected once, here. Nothing else in this app touches a
 * global. There is no mock transport in the shipped code: every screen is
 * developed and verified against the real seeded server.
 */
const transport: FetchImpl = (input: string, init: Parameters<FetchImpl>[1]) =>
  window.fetch(input, init as RequestInit) as unknown as ReturnType<FetchImpl>;

/** The machine code is logged and never shown ([T-73]). */
function logRefusal(rejection: ApiRejection): void {
  if (typeof console !== 'undefined') {
    console.warn('[refused]', rejection.status, rejection.body.code, rejection.body.violated);
  }
}

const client = createApiClient({
  baseUrl: '',
  fetchImpl: transport,
  auth: { kind: 'cookie' },
  onRejection: logRefusal,
});

export type ApiClient = typeof client;

/* ==========================================================================
 * The typed surface the screens use. One cast per endpoint, here and nowhere
 * else, against the shapes declared in ./contracts.ts.
 * ======================================================================== */

export const api = {
  /* ------------------------------------------------------------- session */
  signIn: (body: SignInRequest): Promise<SessionResponse> => client.signIn(body),
  currentSession: (): Promise<SessionResponse> => client.currentSession(),
  signOut: (): Promise<void> => client.signOut(),
  requestPasswordReset: (identifier: string) => client.requestPasswordReset(identifier),

  /* -------------------------------------------------------- client rooms */
  home: (): Promise<HomePayload> => client.home().then((raw) => adaptHome(raw as never)),
  measurements: () => client.measurements() as unknown as Promise<MeasurementsPayload>,
  measurementRecord: (recordId: string) =>
    client.measurementRecord(recordId) as Promise<MeasurementsPayload>,
  requestRemeasure: (body: { preferredTiming?: string }) =>
    client.requestRemeasure(body) as Promise<{ recordedAt: string; existingAt?: string }>,
  privacyRequest: (body: { kind: 'export' | 'erasure' }) =>
    client.privacyRequest(body) as Promise<{ recordedAt: string }>,

  /* ------------------------------------------------------------- the book */
  fabrics: (query: Record<string, unknown> = {}): Promise<FabricListPayload> =>
    client.fabrics(query).then((raw) => adaptFabricList(raw as never)),
  fabric: (clothId: string, query: { draftId?: string } = {}): Promise<ClothDetailPayload> =>
    client.fabric(clothId, query).then((raw) => adaptClothDetail(raw as never)),
  requestSwatch: (body: { clothId: string; address?: string }) =>
    client.requestSwatch(body) as Promise<{ recordedAt: string; existingAt?: string }>,
  registerClothInterest: (body: { clothId: string }) =>
    client.registerClothInterest(body) as Promise<{ recordedAt: string }>,

  /* ------------------------------------------------------ the commission */
  createDraft: (body: { commissionType?: string; clothId?: string }) => client.createDraft(body),
  draft: (draftId: string) => client.draft(draftId) as Promise<EvaluationPayload>,
  deleteDraft: (draftId: string) => client.deleteDraft(draftId),
  evaluate: (draftId: string, body: ConfigEvalRequest) =>
    client.evaluate(draftId, body) as unknown as Promise<EvaluationPayload>,
  /**
   * Review composes two calls: its own authoritative quote, then the cloth it
   * is bound to. `API-REVIEW-GET` does not carry the cloth's own facts, and the
   * screen states them in full (`[T-58]`) rather than hiding what it has.
   */
  review: async (draftId: string): Promise<ReviewPayload> => {
    const raw = (await client.review(draftId)) as Record<string, unknown>;
    const context = raw['clothContext'] as { clothId?: string } | null | undefined;
    const clothId = context?.clothId;
    const cloth =
      clothId === undefined
        ? null
        : await client.fabric(clothId, { draftId }).then((c) => adaptClothDetail(c as never));
    return adaptReview(raw, cloth);
  },
  placeOrder: (body: PlaceOrderRequest): Promise<PlaceOrderResponse> => client.placeOrder(body),
  orders: () => client.orders() as Promise<{ orders: readonly OrderSummary[] }>,
  order: (orderRef: string): Promise<OrderPayload> =>
    client.order(orderRef).then((raw) => adaptOrder(raw as never)),
  askAboutOrder: (orderRef: string, body: { kind: 'change' | 'cancel'; message: string }) =>
    client.askAboutOrder(orderRef, body) as Promise<{ recordedAt: string }>,

  /* ------------------------------------------------------------- atelier */
  atelierOrders: (query: Record<string, unknown> = {}) =>
    client.atelierOrders(query) as Promise<QueuePayload>,
  atelierOrder: (orderRef: string): Promise<AtelierOrderPayload> =>
    client.atelierOrder(orderRef).then((raw) => adaptAtelierOrder(raw as never)),
  transition: (orderId: string, body: TransitionRequest): Promise<TransitionResponse> =>
    client.transition(orderId, body),
  addNote: (orderId: string, body: { note: string; visibility: 'internal' | 'client_visible' }) =>
    client.addNote(orderId, body),
  assign: (orderId: string, body: { assignedCutter: string | null }) =>
    client.assign(orderId, body),
  promise: (orderId: string, body: PromiseRequest) => client.promise(orderId, body),
  recordCutSheetPrint: (orderId: string, body: { format: string }) =>
    client.recordCutSheetPrint(orderId, body),
  /* LAW 59 · the cut tickets of one order, with the photographs that print with them, and the sketch on one, appended. */
  cutTickets: (orderRef: string) => client.cutTickets(orderRef) as Promise<CutTicketsResponse>,
  saveTicketSketch: (orderId: string, garment: string, body: SketchDrawing) =>
    client.saveTicketSketch(orderId, garment, body) as Promise<{ sketch: TicketSketch }>,
  atelierClients: (query: { search?: string } = {}) =>
    client.atelierClients(query) as Promise<{ clients: readonly AtelierClientRow[] }>,
  atelierClient: (clientId: string) =>
    client.atelierClient(clientId) as Promise<AtelierClientPayload>,

  /* ------------------------------------------------ the site's commissions (WIRE-1) */
  leads: (query: { status?: string } = {}) =>
    client.leads(query) as Promise<{ status: string; leads: readonly LeadRow[] }>,
  lead: (leadId: string) => client.lead(leadId) as Promise<{ lead: LeadDetail }>,
  leadToClient: (leadId: string, body: { clientId?: string }) =>
    client.leadToClient(leadId, body) as Promise<{
      leadId: string;
      clientId: string;
      clientRef: string;
      created: boolean;
      replayed: boolean;
    }>,
  closeLead: (leadId: string, body: { reason: string }) =>
    client.closeLead(leadId, body) as Promise<{ leadId: string; status: string; replayed: boolean }>,

  /* ------------------------------------------------- the record, on the road
   * P-CLIENT-01 / P-PHOTO-01 / P-ROAD-IDEM-01. Every write here takes the key
   * the screen was opened with: this app runs on an iPad in somebody's office,
   * and a second tap on a bad connection must not make a second client, a
   * second record or a second set of photographs. */
  createClient: (body: {
    displayName: string;
    email?: string;
    deliveryAddress?: string;
    contact?: string;
    idempotencyKey?: string;
  }) =>
    client.createClient(body) as Promise<{
      clientId: string;
      clientRef: string;
      hasPortalIdentifier: boolean;
      portalStatement: string;
      replayed?: boolean;
    }>,
  amendClient: (clientId: string, body: { field: string; value: string; reason?: string }) =>
    client.amendClient(clientId, body) as Promise<{ seq: number; amendedByName: string }>,
  addClientNote: (clientId: string, body: { body: string }) =>
    client.addClientNote(clientId, body) as Promise<{ clientNoteId: string; seq: number }>,
  photographSets: (clientId: string) =>
    client.photographSets(clientId) as Promise<PhotographSetsPayload>,
  createPhotographSet: (
    clientId: string,
    body: {
      takenAt?: string;
      takenAtPlace?: string;
      note?: string;
      photographs: readonly { pose: string; dataBase64: string }[];
      consent?: { statementText: string; statementVersion?: number };
      idempotencyKey?: string;
    },
  ) =>
    client.createPhotographSet(clientId, body) as Promise<{
      set: PhotographSet;
      housePoses: readonly string[];
      replayed: boolean;
    }>,
  /**
   * The bytes of one photograph. A URL rather than a fetch, so an `<img>` asks
   * for it and the browser carries the session cookie — same origin, no token
   * in a query string, and the read is audit-logged with the reader's name like
   * every other read of a body.
   */
  photographUrl: (photographId: string) =>
    client.urlFor('API-ATELIER-PHOTOGRAPH-GET', { photographId }),
  atelierMeasurementRecords: (clientId: string) =>
    client.atelierMeasurementRecords(clientId) as Promise<MeasurementsPayload>,
  createMeasurementRecord: (clientId: string, body: CreateMeasurementRecordRequest) =>
    client.createMeasurementRecord(clientId, body) as Promise<{ recordId: string }>,
  lockMeasurementRecord: (recordId: string) =>
    client.lockMeasurementRecord(recordId) as Promise<{ lockedAt: string }>,
  verifyMeasurementRecord: (recordId: string) => client.verifyMeasurementRecord(recordId),
  atelierCloths: () => client.atelierCloths() as Promise<{ cloths: readonly AtelierClothRow[] }>,
  atelierCloth: (clothId: string) => client.atelierCloth(clothId) as Promise<AtelierClothPayload>,
  moveStock: (clothId: string, body: StockMoveRequest) =>
    client.moveStock(clothId, body) as Promise<{ balanceM: number }>,
  setClothLifecycle: (
    clothId: string,
    body: { lifecycleState: 'published' | 'unpublished' | 'discontinued'; reason?: string },
  ) => client.setClothLifecycle(clothId, body),
  swatchRequests: () =>
    client.swatchRequests() as Promise<{
      requests: readonly SwatchQueueRow[];
      interests: readonly ClothInterestRow[];
    }>,
  markSwatchSent: (id: string) => client.markSwatchSent(id),

  /* ── ROAD-2 · the money ─────────────────────────────────────────────── */
  paymentMethods: () => client.paymentMethods() as Promise<PaymentMethodsPayload>,
  payments: (orderId: string) => client.payments(orderId) as Promise<PaymentsPayload>,
  createPayment: (
    orderId: string,
    body: { purpose: string; amountMinor: number; processor: string; idempotencyKey?: string },
  ) => client.createPayment(orderId, body) as Promise<PaymentCreated>,
  retention: () => client.retention() as Promise<RetentionPayload>,
  glossary: (query: { status?: string } = {}) =>
    client.glossary(query) as Promise<GlossaryPayload>,
  signGlossaryTerm: (termKey: string, body: { th: string; note?: string }) =>
    client.signGlossaryTerm(termKey, body) as Promise<{ term: GlossaryTerm }>,
  recordPaymentEvent: (
    paymentIntentId: string,
    body: { kind: string; amountMinor?: number; note?: string; idempotencyKey?: string },
  ) => client.recordPaymentEvent(paymentIntentId, body) as Promise<{ intent: PaymentIntentRow }>,
  clothInterests: () => client.clothInterests() as Promise<{ interests: readonly ClothInterestRow[] }>,
};

/** A stable idempotency key for a transition (`ST-TRANS-BUSY` — never double-submits). */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}
